import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getDisplayPrice } from '../../utils/getDisplayPrice';
import { formatPrice } from '../../utils/formatPrice';
import { CategoryInterfaces, PassengerInterfaces } from './CommonInterfaces';

const PULSE_CLASS = 'gordian-seat-pulse';
const CHIP_PULSE_CLASS = 'gordian-chip-intro';
const PULSE_DURATION_MS = 1400;

function injectPulseStyles() {
  if (document.getElementById('gordian-seat-pulse-styles')) return;
  const style = document.createElement('style');
  style.id = 'gordian-seat-pulse-styles';
  style.textContent = `
    @keyframes gordian-seat-ping {
      0%   { box-shadow: 0 0 0 0 rgba(255,255,255,0.85); transform: scale(1); }
      50%  { box-shadow: 0 0 0 6px rgba(255,255,255,0); transform: scale(1.15); }
      100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); transform: scale(1); }
    }
    .${PULSE_CLASS} {
      animation: gordian-seat-ping 0.46s ease-in-out 3;
      position: relative;
      z-index: 60 !important;
    }
    @keyframes gordian-chip-intro-pulse {
      0%   { box-shadow: 0 0 0 0 rgba(107, 114, 128, 0.45); }
      65%  { box-shadow: 0 0 0 6px rgba(107, 114, 128, 0); }
      100% { box-shadow: 0 0 0 0 rgba(107, 114, 128, 0); }
    }
    .${CHIP_PULSE_CLASS} {
      animation: gordian-chip-intro-pulse 0.75s ease-out 2;
    }
  `;
  document.head.appendChild(style);
}

interface CategoryStripProps {
  currentSeatmap: string;
  categories: CategoryInterfaces.Categories;
  currentPassenger: PassengerInterfaces.Passenger;
  priceAdjustment: { [key: string]: any };
  variants: { [variantKey: string]: any };
  notAvailableFill: string;
}

class CategoryStrip extends Component<
  CategoryStripProps,
  { chipsAnimated: boolean }
> {
  state = { chipsAnimated: false };
  private _pulseTimer: ReturnType<typeof setTimeout> | null = null;
  private _clearTimer: ReturnType<typeof setTimeout> | null = null;

  componentDidMount() {
    injectPulseStyles();
    this._pulseTimer = setTimeout(
      () => this.setState({ chipsAnimated: true }),
      350
    );
    this._clearTimer = setTimeout(
      () => this.setState({ chipsAnimated: false }),
      2800
    );
  }

  componentWillUnmount() {
    if (this._pulseTimer) clearTimeout(this._pulseTimer);
    if (this._clearTimer) clearTimeout(this._clearTimer);
  }

  handleCategoryClick = (categoryId: string) => {
    const seats = Array.from(
      document.querySelectorAll(`[data-category-id="${categoryId}"]`)
    ) as HTMLElement[];

    if (seats.length === 0) return;

    // Scroll first seat into view within the seatmap container
    seats[0].scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Pulse all matching seats
    seats.forEach((seat) => seat.classList.add(PULSE_CLASS));
    setTimeout(() => {
      seats.forEach((seat) => seat.classList.remove(PULSE_CLASS));
    }, PULSE_DURATION_MS);
  };

  render(): JSX.Element {
    const {
      currentSeatmap,
      categories,
      currentPassenger,
      priceAdjustment = {},
      variants,
      notAvailableFill,
    } = this.props;

    if (!categories || !currentSeatmap) return <></>;

    const seatmapCategories = categories[currentSeatmap];
    if (!seatmapCategories) return <></>;

    const seatLegendText =
      variants?.convenienceFeeConfiguration?.seats?.seatLegendText;
    const priceFactor = priceAdjustment.factor ?? 1.0;

    const { chipsAnimated } = this.state;
    const pills: JSX.Element[] = [];
    let chipIdx = 0;

    for (const key in seatmapCategories) {
      const cat = seatmapCategories[key];
      let priceLabel = '';

      if (
        currentPassenger?.passenger_id &&
        cat.price_range?.[currentPassenger.passenger_id]
      ) {
        const { start_price: startPrice, end_price: endPrice } = cat
          .price_range[currentPassenger.passenger_id] as any;

        const startDisplay = getDisplayPrice(
          seatLegendText,
          startPrice.base_price,
          startPrice.total
        );
        const endDisplay = getDisplayPrice(
          seatLegendText,
          endPrice.base_price,
          endPrice.total
        );
        const startStr = formatPrice(
          startDisplay * priceFactor,
          startPrice.currency,
          startPrice.decimal_places
        );
        const endStr = formatPrice(
          endDisplay * priceFactor,
          endPrice.currency,
          endPrice.decimal_places
        );

        if (startStr === endStr) {
          priceLabel = startStr;
        } else {
          priceLabel = `${startStr}–${endStr}`;
        }
      }

      const delay = chipIdx * 0.1;
      pills.push(
        <button
          key={key}
          onClick={() => this.handleCategoryClick(key)}
          className={`gr-flex gr-items-center gr-whitespace-nowrap gr-rounded-full gr-border gr-border-gray-200 gr-px-4 gr-text-sm gr-font-medium gr-text-gray-700 gr-shadow-sm gr-flex-shrink-0 gr-cursor-pointer${chipsAnimated ? ` ${CHIP_PULSE_CLASS}` : ''}`}
          style={{
            gap: '8px',
            paddingTop: '10px',
            paddingBottom: '10px',
            backgroundColor: '#ffffff',
            transition:
              'transform 0.12s ease, background-color 0.12s ease, border-color 0.12s ease',
            animationDelay: chipsAnimated ? `${delay}s` : '0s',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = '#F9FAFB';
            (e.currentTarget as HTMLElement).style.borderColor = '#D1D5DB';
            (e.currentTarget as HTMLElement).style.transform = 'scale(1.04)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = '#ffffff';
            (e.currentTarget as HTMLElement).style.borderColor = '';
            (e.currentTarget as HTMLElement).style.transform = '';
          }}
          onMouseDown={(e) => {
            (e.currentTarget as HTMLElement).style.transform = 'scale(0.97)';
          }}
          onMouseUp={(e) => {
            (e.currentTarget as HTMLElement).style.transform = 'scale(1.04)';
          }}
          aria-label={`Scroll to ${cat.name} seats`}
        >
          <span
            className="gr-flex-shrink-0 gr-rounded-full"
            style={{ width: '12px', height: '12px', backgroundColor: cat.fill }}
          />
          <span>{cat.name}</span>
          {priceLabel && (
            <>
              <span style={{ color: '#D1D5DB' }}>·</span>
              <span className="gr-font-semibold gr-text-gray-900">
                {priceLabel}
              </span>
            </>
          )}
        </button>
      );
      chipIdx++;
    }

    // Unavailable seat pill (non-interactive)
    pills.push(
      <div
        key="unavailable"
        className="gr-flex gr-items-center gr-whitespace-nowrap gr-rounded-full gr-border gr-border-gray-200 gr-bg-white gr-px-4 gr-text-sm gr-font-medium gr-text-gray-500 gr-shadow-sm gr-flex-shrink-0"
        style={{ gap: '8px', paddingTop: '10px', paddingBottom: '10px' }}
      >
        <span
          className="gr-flex-shrink-0 gr-rounded-full"
          style={{
            width: '12px',
            height: '12px',
            backgroundColor: notAvailableFill || '#E2E8F0',
          }}
        />
        <span>Unavailable</span>
      </div>
    );

    return (
      <div
        className="gr-sticky gr-top-0 gr-z-10 gr-flex gr-gap-2 gr-overflow-x-auto gr-border-b gr-border-gray-200 gr-bg-white gr-px-4"
        style={{
          paddingTop: '10px',
          paddingBottom: '10px',
          scrollbarWidth: 'none',
        }}
        aria-label="Seat categories"
      >
        {pills}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  currentSeatmap: state.itinerary.currentSeatmap,
  categories: state.categories,
  currentPassenger: state.passengers.currentPassenger,
  priceAdjustment: state.session.priceAdjustment,
  variants: state.session.variants,
  notAvailableFill: state.session.theme?.seats?.['not_available']?.fill,
});

export default connect(mapStateToProps)(CategoryStrip);
